import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} slides
 * @property {HTMLElement[]} dots
 */

/**
 * Autoplaying, dot-navigated hero slideshow for the homepage.
 * Pauses autoplay entirely when prefers-reduced-motion is set (slides can
 * still be changed manually via the dots).
 *
 * @extends {Component<Refs>}
 */
class HeroSlideshowComponent extends Component {
  requiredRefs = ['slides[]', 'dots[]'];

  /** @type {number} */
  current = 0;

  /** @type {boolean} */
  reducedMotion = false;

  /** @type {number} */
  intervalMs = 5000;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  timer = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.current = this.refs.slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (this.current < 0) this.current = 0;

    // Liquid側ではどのスライドが最初かを決めていないため、ここで明示的に
    // 初期状態のis-active/activeクラスを付与する(単一の情報源にするため)。
    this.refs.slides[this.current]?.classList.add('is-active');
    this.refs.dots[this.current]?.classList.add('active');

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.intervalMs = Number(this.dataset.intervalMs) || 5000;
    this.timer = undefined;

    this.refs.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goTo(index);
        this.#resetAutoplay();
      });
    });

    if (this.refs.slides.length > 1 && !this.reducedMotion) {
      this.#startAutoplay();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    clearInterval(this.timer);
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    if (index === this.current) return;
    this.refs.slides[this.current]?.classList.remove('is-active');
    this.refs.dots[this.current]?.classList.remove('active');
    this.current = index;
    this.refs.slides[this.current]?.classList.add('is-active');
    this.refs.dots[this.current]?.classList.add('active');
  }

  #startAutoplay() {
    this.timer = setInterval(() => {
      this.goTo((this.current + 1) % this.refs.slides.length);
    }, this.intervalMs);
  }

  #resetAutoplay() {
    clearInterval(this.timer);
    if (!this.reducedMotion) this.#startAutoplay();
  }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshowComponent);
}
